/**
 * PlacementDetailModal — the dialog a pin opens on double-click.
 *
 * Holds the surveyed install detail for one camera on one layout: where it points, how wide
 * it sees, how high it's mounted, how far it's tilted, and a free-text placement note. All
 * of it reads back on the pin's hover card, so a survey done once answers "which camera
 * covers the north door, and can it see a face at that height?" without a site visit.
 *
 * Height and tilt are deliberately BLANKABLE. An empty field saves null — "not surveyed" —
 * rather than 0, which would assert a camera at floor level aimed straight ahead.
 *
 * Also the entry point to the two placement actions: reposition (drops into the drag
 * editor with this pin selected) and remove from layout.
 */
import { useEffect, useState } from 'react';
import { ArrowsPointingOutIcon, TrashIcon } from '@heroicons/react/24/outline';
import Modal from '../common/Modal';
import type { MapPlacement } from '../../api/contracts';

/** Compass label for a bearing, so "215°" also reads as "SW". */
export function bearingLabel(deg: number): string {
  const points = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return points[Math.round(((deg % 360) + 360) % 360 / 22.5) % 16];
}

/** '' → null (not surveyed); anything else → a number. Keeps the two apart. */
function toNullableNumber(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export interface PlacementDetailModalProps {
  open: boolean;
  placement: MapPlacement | null;
  cameraName: string;
  /** Caller holds `map.edit`; otherwise the dialog is read-only. */
  canEdit?: boolean;
  /** True while the parent is writing to the node. */
  saving?: boolean;
  error?: string;
  onClose: () => void;
  onSave: (patch: Partial<MapPlacement>) => void;
  onRemove: () => void;
  /** Enter the drag editor with this pin selected. */
  onReposition: () => void;
}

export default function PlacementDetailModal({
  open,
  placement,
  cameraName,
  canEdit = false,
  saving = false,
  error,
  onClose,
  onSave,
  onRemove,
  onReposition,
}: PlacementDetailModalProps) {
  const [rotation, setRotation] = useState(0);
  const [fov, setFov] = useState(90);
  const [height, setHeight] = useState('');
  const [tilt, setTilt] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open || !placement) return;
    setRotation(placement.rotation ?? 0);
    setFov(placement.fov_deg ?? 90);
    setHeight(placement.mount_height_m == null ? '' : String(placement.mount_height_m));
    setTilt(placement.tilt_deg == null ? '' : String(placement.tilt_deg));
    setNote(placement.location_note || '');
  }, [open, placement]);

  if (!placement) return null;

  const readOnly = !canEdit;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={cameraName}
      subtitle={readOnly ? 'Camera placement' : 'Camera placement on this layout'}
      size="md"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {canEdit && (
              <>
                <button
                  type="button"
                  onClick={onReposition}
                  className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <ArrowsPointingOutIcon className="h-3.5 w-3.5" /> Reposition
                </button>
                <button
                  type="button"
                  onClick={onRemove}
                  className="flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <TrashIcon className="h-3.5 w-3.5" /> Remove
                </button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              {readOnly ? 'Close' : 'Cancel'}
            </button>
            {canEdit && (
              <button
                type="button"
                disabled={saving}
                onClick={() =>
                  onSave({
                    rotation,
                    fov_deg: fov,
                    mount_height_m: toNullableNumber(height),
                    tilt_deg: toNullableNumber(tilt),
                    location_note: note.trim() || null,
                  })
                }
                className="rounded-lg bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* The Modal shell is padding-free by design — content owns its inset. */}
      <div className="space-y-4 px-5 py-4">
        <div>
          <div className="mb-1 flex items-baseline justify-between">
            <label className="text-xs font-medium text-muted-foreground">Facing</label>
            <span className="text-xs font-semibold tabular-nums text-foreground">
              {Math.round(rotation)}° {bearingLabel(rotation)}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={359}
            disabled={readOnly}
            value={rotation}
            onChange={(e) => setRotation(Number(e.target.value))}
            className="w-full accent-[color:var(--brand,#015ab5)] disabled:opacity-50"
          />
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            0° is up on the plan. The pin&apos;s notch and coverage cone follow this.
          </p>
        </div>

        <div>
          <div className="mb-1 flex items-baseline justify-between">
            <label className="text-xs font-medium text-muted-foreground">Field of view</label>
            <span className="text-xs font-semibold tabular-nums text-foreground">{Math.round(fov)}°</span>
          </div>
          <input
            type="range"
            min={10}
            max={360}
            step={5}
            disabled={readOnly}
            value={fov}
            onChange={(e) => setFov(Number(e.target.value))}
            className="w-full accent-[color:var(--brand,#015ab5)] disabled:opacity-50"
          />
          <p className="mt-0.5 text-[10px] text-muted-foreground">
            How wide the cone spreads. 360° draws a full circle for a fisheye or PTZ.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Mounting height (m)</label>
            <input
              type="number"
              min={0}
              step="0.1"
              disabled={readOnly}
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="Not surveyed"
              className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none disabled:opacity-60"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Downward tilt (°)</label>
            <input
              type="number"
              min={-90}
              max={90}
              step="1"
              disabled={readOnly}
              value={tilt}
              onChange={(e) => setTilt(e.target.value)}
              placeholder="Not surveyed"
              className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none disabled:opacity-60"
            />
          </div>
        </div>
        {/* Leaving either blank is a real answer — the hover card then reads "—" rather
            than claiming a floor-level, level-aimed camera. */}
        <p className="-mt-2 text-[10px] text-muted-foreground">Leave blank if not surveyed.</p>

        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Location note</label>
          <textarea
            rows={2}
            disabled={readOnly}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Above door 3, north-east corner…"
            className="w-full resize-none rounded-md border border-border bg-background px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand focus:outline-none disabled:opacity-60"
          />
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    </Modal>
  );
}
